import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
    ISportsBettingBaseApi,
    IGroupedApi,
    createGroupedApi,
    sportsBettingBaseApi,
    MobileSearchResponse,
    LiveEvents,
    SportsBettingApiResult,
    IConnectionMonitorService,
    ILoggingService,
    IConnectSportsBettingBaseApiParams,
    prepareAxiosConfig,
    ISBProtobufApiOperationResponse,
    CalendarEvents,
    CalendarEventsWithTs, ICalendarEventsWithTs,
    IRecaptchaProvider
} from 'eTip--common';
import { ILiveAppSettings } from '../../liveAppEnvironment';

export interface ISportsBettingLiveApi extends ISportsBettingBaseApi {
    getLiveEventsWLAsync: (timestamp: number) => Promise<LiveEvents.RootObject>;
    GetLiveEventsInPrematchDesktopAsync: (timestamp: number) => Promise<LiveEvents.RootObject>;
    getCalendarAsync: (date: Date, timestamp: number) => Promise<ICalendarEventsWithTs>;
    getLiveSearchAsync: (text: string) => Promise<CalendarEvents>;
}

export function connectSportsBettingLiveGroupedApi(params: IConnectSportsBettingApiParams, loggingService: ILoggingService, connectionMonitorService: IConnectionMonitorService, recaptchaProvider: IRecaptchaProvider): IGroupedApi<ISportsBettingLiveApi> {
    return createGroupedApi(token => {
        return connectSportsBettingApi(params, loggingService, connectionMonitorService, recaptchaProvider, prepareAxiosConfig(token, params.settings.isDebug));
    });
}

export interface IConnectSportsBettingApiParams extends IConnectSportsBettingBaseApiParams {
    settings: ILiveAppSettings;
}

export function connectSportsBettingApi(apiParams: IConnectSportsBettingApiParams, loggingService: ILoggingService, connectionMonitorService: IConnectionMonitorService
    , recaptchaProvider: IRecaptchaProvider, defaultConfig?: AxiosRequestConfig): ISportsBettingLiveApi {
    const baseUrl = `${apiParams.baseUrl}/WebServices/Api/SportsBettingService.svc`;
    const base: ISportsBettingBaseApi = sportsBettingBaseApi(apiParams, loggingService, connectionMonitorService, recaptchaProvider, defaultConfig);
    const processApiResponse = base.processApiResponse;
    const processError = base.processError;
    //const dateToString = apiParams.dateTimeFormater.toString;
    const languageId = apiParams.settings.language.id;

    return Object.assign(base, {
        getLiveEventsWLAsync: (timestamp: number): Promise<LiveEvents.RootObject> => {
            return new Promise<LiveEvents.RootObject>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetLiveEventsWL`,
                        {
                            ActualEvents: true,
                            LanguageID: languageId,
                            Token: apiParams.token,
                            UseLongPolling: true,
                            TimeStamp: timestamp
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<LiveEvents.RootObject>) => {
                        if (processApiResponse(response, r)) {
                            f(response.data);
                        }
                    })
                    .catch(error => {
                        processError(error, r);
                    });
            });
        },
        GetLiveEventsInPrematchDesktopAsync: (timestamp: number): Promise<LiveEvents.RootObject> => {
            return new Promise<LiveEvents.RootObject>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetLIPEvtsDsk`,
                        {
                            ActualEvents: true,
                            LanguageID: languageId,
                            Token: apiParams.token,
                            UseLongPolling: true,
                            TimeStamp: timestamp
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<LiveEvents.RootObject>) => {
                        if (processApiResponse(response, r)) {
                            f(response.data);
                        }
                    })
                    .catch(error => {
                        processError(error, r);
                    });
            });
        },
        getCalendarAsync: (date: Date, timestamp: number) => {
            return new Promise<ICalendarEventsWithTs>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetLiveCalendar`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            UseLongPolling: false,
                            TimeStamp: timestamp,
                            Day: base.dateToString(date)
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<ISBProtobufApiOperationResponse>) => {
                        if (processApiResponse(response, r)) {
                            if (response.data.Result === SportsBettingApiResult.NoChange) {
                                f(null);
                            } else {
                                let responseValue = base.isEncodedNull(response.data.ReturnValue) ? null : CalendarEventsWithTs.decode(base.createBuffer(response.data.ReturnValue));
                                f(responseValue);
                            }
                        }
                    })
                    .catch(error => {
                        processError(error, r);
                    });
            });
        },
        getSearchAsync: (text: string) => {
            return new Promise<MobileSearchResponse>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/MobileSearch`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            SearchTerm: text
                        },
                        defaultConfig
                    )
                    .then(response => {
                        if (processApiResponse(response, r)) {
                            if (response.data.Result === SportsBettingApiResult.Success) {
                                f(MobileSearchResponse.decode(base.createBuffer(response.data.ReturnValue)));
                            }
                        }
                    })
                    .catch(error => {
                        processError(error, r);
                    });
            });
        },
        getLiveSearchAsync: (text: string) => {
            return new Promise<CalendarEvents>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetLiveSearch`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            Text: text
                        },
                        defaultConfig
                    )
                    .then(response => {
                        if (processApiResponse(response, r)) {
                            if (response.data.Result === SportsBettingApiResult.Success) {
                                f(CalendarEvents.decode(base.createBuffer(response.data.ReturnValue)));
                            }
                        }
                    })
                    .catch(error => {
                        processError(error, r);
                    });
            });
        }
    })
}
